export const sameThreadId = (left, right) =>
  left !== null &&
  left !== undefined &&
  right !== null &&
  right !== undefined &&
  String(left).trim() === String(right).trim();

export const isAirbnbSource = (source) =>
  String(source?.ota || "").trim().toLowerCase() === "airbnb";

export const getAirbnbThreadIds = (sources) => {
  const ids = new Map();

  (Array.isArray(sources) ? sources : []).forEach((source) => {
    if (!isAirbnbSource(source)) return;

    const key = String(source?.ota_id ?? "").trim();
    if (key && !ids.has(key)) ids.set(key, source.ota_id);
  });

  return Array.from(ids.values());
};

export const attachAirbnbThreadsToSources = (sources, airbnbThreads) =>
  (Array.isArray(sources) ? sources : []).map((source) => {
    if (!isAirbnbSource(source)) return source;

    const airbnbThread = (Array.isArray(airbnbThreads) ? airbnbThreads : [])
      .find((thread) => sameThreadId(thread?.thread_id, source?.ota_id));

    return airbnbThread ? { ...source, thread: airbnbThread } : source;
  });

export const getThreadDetailId = (threadDetail) =>
  threadDetail?.id ?? threadDetail?.thread_id ?? null;

export const threadHasMessage = (threadDetail, messageId) =>
  messageId !== null &&
  messageId !== undefined &&
  (threadDetail?.messages || []).some(
    (message) => String(message.message_id) === String(messageId),
  );

const messageTimestamp = (message) => {
  const timestamp = Date.parse(message?.created_at);
  return Number.isFinite(timestamp) ? timestamp : null;
};

const messageContentMatches = (pending, confirmed) => {
  const pendingContent = String(pending?.content || "");
  const confirmedContent = String(confirmed?.content || "");
  // The API trims the body before appending an enabled SMS signature, so a
  // composed message with surrounding whitespace never matches its own
  // persisted copy unless we compare against the trimmed content too.
  const trimmedPendingContent = pendingContent.trim();

  return (
    confirmedContent === pendingContent ||
    confirmedContent === trimmedPendingContent ||
    (!!trimmedPendingContent &&
      confirmedContent.startsWith(`${trimmedPendingContent}\n\n`))
  );
};

const PENDING_MESSAGE_CLOCK_SKEW_MS = 30 * 1000;
const PENDING_MESSAGE_RECONCILIATION_WINDOW_MS = 15 * 60 * 1000;

export const pendingMessageMatches = (pending, confirmed) => {
  if (!pending?.client_message_id || !confirmed?.message_id) return false;
  if (
    pending.thread_id !== null &&
    pending.thread_id !== undefined &&
    confirmed.thread_id !== null &&
    confirmed.thread_id !== undefined &&
    !sameThreadId(pending.thread_id, confirmed.thread_id)
  ) {
    return false;
  }

  const knownMessageIds = new Set(
    (pending.known_message_ids || []).map(String),
  );
  if (knownMessageIds.has(String(confirmed.message_id))) return false;

  if (confirmed.client_message_id) {
    return (
      String(pending.client_message_id) ===
      String(confirmed.client_message_id)
    );
  }

  if (
    pending.sender_type &&
    confirmed.sender_type &&
    String(pending.sender_type) !== String(confirmed.sender_type)
  ) {
    return false;
  }
  if (
    pending.sender_id !== null &&
    pending.sender_id !== undefined &&
    confirmed.sender_id !== null &&
    confirmed.sender_id !== undefined &&
    String(pending.sender_id) !== String(confirmed.sender_id)
  ) {
    return false;
  }
  if (!messageContentMatches(pending, confirmed)) return false;

  const pendingAt = messageTimestamp(pending);
  const confirmedAt = messageTimestamp(confirmed);
  if (pendingAt === null || confirmedAt === null) return false;

  return (
    confirmedAt >= pendingAt - PENDING_MESSAGE_CLOCK_SKEW_MS &&
    confirmedAt <= pendingAt + PENDING_MESSAGE_RECONCILIATION_WINDOW_MS
  );
};

export const mergeThreadMessages = (previousMessages, incomingMessages) => {
  const previous = Array.isArray(previousMessages) ? previousMessages : [];
  const incoming = Array.isArray(incomingMessages) ? incomingMessages : [];
  const confirmedById = new Map();

  [...previous, ...incoming].forEach((message) => {
    if (message?.message_id !== null && message?.message_id !== undefined) {
      confirmedById.set(String(message.message_id), message);
    }
  });

  const confirmed = Array.from(confirmedById.values());
  const pending = previous.filter(
    (message) =>
      message?.client_message_id &&
      (message.message_id === null || message.message_id === undefined),
  );
  const unmatchedPending = pending.filter(
    (message) =>
      !confirmed.some((confirmedMessage) =>
        pendingMessageMatches(message, confirmedMessage),
      ),
  );

  return [...confirmed, ...unmatchedPending].sort((left, right) => {
    const leftAt = messageTimestamp(left);
    const rightAt = messageTimestamp(right);
    if (leftAt === null || rightAt === null) return 0;
    return leftAt - rightAt;
  });
};

export const mergeThreadDetailSnapshot = (current, incoming) => {
  if (!incoming) return current;

  const currentId = getThreadDetailId(current);
  const incomingId = getThreadDetailId(incoming);
  if (
    currentId !== null &&
    incomingId !== null &&
    !sameThreadId(currentId, incomingId)
  ) {
    return incoming;
  }

  return {
    ...(current || {}),
    ...incoming,
    messages: mergeThreadMessages(current?.messages, incoming?.messages),
  };
};

export const updateThreadDetailIfCurrent = (
  threadDetail,
  selectedThreadId,
  targetThreadId,
  update,
) => {
  if (!sameThreadId(selectedThreadId, targetThreadId)) return threadDetail;

  const detailId = getThreadDetailId(threadDetail);
  const isNewThread = targetThreadId === "new" && detailId === null;
  if (!isNewThread && !sameThreadId(detailId, targetThreadId)) {
    return threadDetail;
  }

  return update(threadDetail);
};

export const appendMessageToCurrentThread = (
  threadDetail,
  selectedThreadId,
  targetThreadId,
  message,
) =>
  updateThreadDetailIfCurrent(
    threadDetail,
    selectedThreadId,
    targetThreadId,
    (current) => {
      const messages = current?.messages || [];
      if (
        message.client_message_id &&
        messages.some(
          (item) => item.client_message_id === message.client_message_id,
        )
      ) {
        return current;
      }

      return { ...current, messages: [...messages, message] };
    },
  );

export const confirmPendingMessageInCurrentThread = (
  threadDetail,
  selectedThreadId,
  targetThreadId,
  confirmedMessage,
) =>
  updateThreadDetailIfCurrent(
    threadDetail,
    selectedThreadId,
    targetThreadId,
    (current) => {
      if (
        !confirmedMessage?.client_message_id ||
        confirmedMessage.message_id === null ||
        confirmedMessage.message_id === undefined
      ) {
        return current;
      }

      let confirmed = false;
      const messages = (current?.messages || []).map((message) => {
        if (
          confirmed ||
          (message?.message_id !== null &&
            message?.message_id !== undefined) ||
          !message?.client_message_id ||
          String(message.client_message_id) !==
            String(confirmedMessage.client_message_id)
        ) {
          return message;
        }

        confirmed = true;
        return {
          ...message,
          message_id: confirmedMessage.message_id,
          status: message.status === "sending" ? "sent" : message.status,
        };
      });

      return confirmed ? { ...current, messages } : current;
    },
  );

export const removeMessageFromCurrentThread = (
  threadDetail,
  selectedThreadId,
  targetThreadId,
  clientMessageId,
) =>
  updateThreadDetailIfCurrent(
    threadDetail,
    selectedThreadId,
    targetThreadId,
    (current) => ({
      ...current,
      messages: (current?.messages || []).filter(
        (message) => message.client_message_id !== clientMessageId,
      ),
    }),
  );
